import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Award,
  Sparkles,
  CheckCircle2,
  Lock,
  ShieldCheck,
  TrendingUp,
  Briefcase,
  Rocket,
  ArrowRight,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import { supabase } from '../../lib/supabase';
import AuthModal from '../../components/auth/auth-modal';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';
import type { User } from '@supabase/supabase-js';

// — Typography aligned —
const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';
const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';
const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';
const h3Class = 'text-xl font-semibold text-[#30475e] mb-3 leading-snug';
const bodyClass = 'text-base text-neutral-600 leading-relaxed';
const sectionSubtitleClass =
  'text-lg md:text-xl text-neutral-600 leading-relaxed';

// — 2 modèles commerciaux fondamentaux ————————————————————————
const businessModels = [
  {
    icon: Award,
    badge: 'Avec retainer',
    title: 'Engagement initial. Sécurisation maximale.',
    description:
      "Vous engagez en amont, nous offrons le Parcours Découverte au candidat. Idéal pour les postes critiques, time-to-hire serré, exigence de sécurisation totale.",
    points: [
      'Engagement initial signé',
      'Plateforme Parcours Découverte incluse',
      'Garantie replacement < 12 mois',
      'Suivi 90 jours post-prise de poste',
    ],
    fitFor: 'Poste critique · Profil rare · Direction · Confidentialité forte',
  },
  {
    icon: Sparkles,
    badge: 'Au succès',
    title: 'Aucun engagement initial. Vous payez si placement.',
    description:
      "Pas de retainer. Production du Parcours Découverte facturée séparément. Idéal pour les volumes, les postes ouverts à plusieurs candidats, les équipes RH déjà rodées.",
    points: [
      "Pas d'engagement initial",
      'Fee uniquement si placement',
      'Production Parcours facturée séparément',
      'Garantie replacement < 6 mois',
    ],
    fitFor: 'Volume · Récurrence · RH internes opérationnels',
  },
];

// — 3 offres détaillées (visibles connecté) —————————————————————
const offers = [
  {
    icon: Briefcase,
    title: '#Essentiel',
    tagline: 'Simple, rapide, garanti',
    description:
      "Pour les entreprises qui veulent recruter efficacement, sans se ruiner. Format au succès, idéal pour un premier recrutement avec recruteurs.io.",
    bullets: [
      'Brief approfondi + sourcing chasse + IA',
      'Plateforme Parcours Découverte (en option)',
      'Garantie replacement < 6 mois',
      'Suivi 90 jours post-prise de poste',
    ],
  },
  {
    icon: ShieldCheck,
    title: '#ModulR',
    tagline: 'Remboursé ou remplacé',
    description:
      "Formule flexible avec garantie de résultat renforcée. Pour les postes critiques où l'erreur de casting coûte cher.",
    bullets: [
      'Engagement retainer modulé',
      'Plateforme Parcours Découverte incluse',
      'Garantie replacement étendue + 30 % fee restitué',
      'Diagnostic post-mortem honnête en cas de rupture',
    ],
  },
  {
    icon: Rocket,
    title: '#Progressive',
    tagline: 'Zéro engagement, zéro stress',
    description:
      "Idéal pour les startups et scale-ups qui veulent grandir sereinement, avec un partenaire qui s'adapte à leur trésorerie et leur cadence.",
    bullets: [
      'Paiement échelonné selon les jalons',
      'Plateforme Parcours Découverte sur-mesure',
      'Adaptable au volume et à la cadence',
      'Idéal scale-up et startups en croissance',
    ],
  },
];

// — 4 critères de variation tarifaire —————————————————————————
const pricingFactors = [
  {
    icon: TrendingUp,
    title: 'Niveau du poste',
    description:
      "Cadre, manager, dirigeant, expert rare. Plus le poste est complexe ou sensible, plus le travail amont est important.",
  },
  {
    icon: Briefcase,
    title: "Secteur d'activité",
    description:
      "Tech, retail, industrie, services. Certains marchés sont plus tendus que d'autres et demandent une approche spécifique.",
  },
  {
    icon: ShieldCheck,
    title: 'Niveau de garantie',
    description:
      "Garantie standard 6 mois, garantie étendue 12 mois, replacement gratuit + restitution partielle de fee. Vous choisissez votre niveau de couverture.",
  },
  {
    icon: Sparkles,
    title: 'Plateforme Parcours Découverte',
    description:
      "Incluse dans certaines formules, facturée séparément dans d'autres. La production sur-mesure prend 2 à 3 semaines selon la richesse du contenu.",
  },
];

export default function TarifsPage() {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
    });
    return () => subscription.unsubscribe();
  }, []);

  return (
    <Layout>
      <Helmet>
        <title>Tarifs cabinet de recrutement | recruteurs.io</title>
        <meta
          name="description"
          content="Découvrez les formules de recrutement Recruteurs.io : 2 modèles (retainer / au succès), 3 offres détaillées, garantie replacement, Plateforme Parcours Découverte. Tarifs transparents adaptés aux PME et ETI."
        />
        <link rel="canonical" href="https://recruteurs.io/infos/tarifs/" />
      </Helmet>

      <WebPageSchema
        page={{
          title: 'Tarifs cabinet de recrutement | recruteurs.io',
          description:
            "2 modèles commerciaux (retainer / au succès), 3 offres détaillées, garantie replacement chiffrée. Recruteurs.io affiche ses formules en transparence.",
          url: 'https://recruteurs.io/infos/tarifs',
          lastReviewed: '2026-05-10',
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Infos', url: 'https://recruteurs.io/infos/tarifs' },
          { name: 'Tarifs', url: 'https://recruteurs.io/infos/tarifs' },
        ]}
      />

      {/* HERO centré */}
      <SectionBackground
        page="tarifs"
        section="t-hero"
        className="relative overflow-hidden bg-gradient-to-br from-[#fff1f2] via-[#fef4e6] to-[#e0f2ff]"
      >
        <section className="relative z-10 min-h-[60vh] flex items-center py-10 md:py-12 lg:py-14">
          <div className="w-full px-[5vw] xl:px-[100px] max-w-[1440px] mx-auto">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-xs md:text-sm font-semibold text-neutral-500 mb-3 leading-snug uppercase tracking-widest">
                Tarifs · 2 modèles commerciaux · 3 offres détaillées
              </p>

              <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-[3.5rem] font-title font-extrabold leading-[1.1] pb-1 mb-0">
                <span className="text-[#30475e]">Nos offres sont simples. </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                  Notre méthode efficace.
                </span>
              </h1>

              <p className="mt-5 text-neutral-800 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                Deux modèles commerciaux fondamentaux ({' '}
                <strong>avec retainer</strong> ou <strong>au succès</strong>),
                trois offres détaillées, et une transparence totale dès le
                premier appel. Nous ne vendons pas des CV — nous sécurisons des
                prises de poste.
              </p>
            </div>
          </div>
        </section>
      </SectionBackground>

      {/* SECTION 2 — Les 2 modèles fondamentaux (publics) */}
      <SectionBackground page="tarifs" section="t-models" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Deux modèles commerciaux</span>
            <h2 className={h2Class}>
              Choisissez celui qui colle{' '}
              <span className={gradientText}>à votre niveau de risque.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Avec retainer pour sécuriser un poste critique en amont. Au
              succès pour piloter un volume sans engagement initial. Les deux
              donnent accès à notre méthode complète.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {businessModels.map((model, index) => {
              const Icon = model.icon;
              return (
                <motion.div
                  key={model.badge}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[2rem] bg-white p-8 md:p-10 ring-1 ring-neutral-200 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className="inline-flex items-center rounded-full bg-[#fbfaf8] px-4 py-1.5 text-xs font-semibold text-neutral-700 uppercase tracking-widest ring-1 ring-neutral-200">
                      {model.badge}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-[#30475e] leading-tight mb-3">
                    {model.title}
                  </h3>
                  <p className={`${bodyClass} mb-5`}>{model.description}</p>

                  <ul className="space-y-2 mb-5">
                    {model.points.map((point) => (
                      <li
                        key={point}
                        className="flex items-start gap-2 text-sm text-neutral-700"
                      >
                        <CheckCircle2 className="w-4 h-4 text-[#d03484] flex-shrink-0 mt-0.5" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="pt-4 border-t border-neutral-100">
                    <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-1">
                      Idéal pour
                    </p>
                    <p className="text-sm text-neutral-700">{model.fitFor}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 3 — 3 offres détaillées (verrouillées) */}
      <SectionBackground page="tarifs" section="t-offers" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Nos trois offres détaillées</span>
            <h2 className={h2Class}>
              Trois packages sur étagère.{' '}
              <span className={gradientText}>Tarifs sur connexion.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Pour respecter la confidentialité tarifaire usuelle dans le
              secteur, les fourchettes détaillées sont accessibles aux
              utilisateurs connectés. Tarifs présentés en clair lors du premier
              appel, sans engagement.
            </p>
          </div>

          <div className="relative max-w-5xl mx-auto">
            <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 ${user ? '' : 'blur-md pointer-events-none select-none'}`}>
              {offers.map((offer, index) => {
                const Icon = offer.icon;
                return (
                  <motion.div
                    key={offer.title}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.5, delay: index * 0.08 }}
                    className="rounded-[2rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm flex flex-col"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#30475e] mb-1">
                      {offer.title}
                    </h3>
                    <p className="text-sm font-semibold text-[#d03484] mb-4">
                      {offer.tagline}
                    </p>
                    <p className={`${bodyClass} mb-5`}>{offer.description}</p>
                    <ul className="space-y-2 mt-auto">
                      {offer.bullets.map((b) => (
                        <li
                          key={b}
                          className="flex items-start gap-2 text-sm text-neutral-700"
                        >
                          <CheckCircle2 className="w-4 h-4 text-[#d03484] flex-shrink-0 mt-0.5" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                );
              })}
            </div>

            {!user && (
              <div className="absolute inset-0 z-10 flex items-center justify-center px-4">
                <div className="bg-white rounded-[2rem] shadow-2xl ring-1 ring-neutral-200 p-8 md:p-10 text-center max-w-md w-full">
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center mx-auto mb-5 shadow-lg shadow-[#d03484]/20">
                    <Lock className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#30475e] mb-2">
                    Connectez-vous pour voir le détail
                  </h3>
                  <p className="text-sm text-neutral-500 mb-6 leading-relaxed">
                    Tarifs, fourchettes, garanties détaillées : accessibles
                    après création de votre compte gratuit (entreprise).
                  </p>
                  <button
                    onClick={() => setIsAuthModalOpen(true)}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
                  >
                    Accéder aux tarifs détaillés
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 4 — 4 critères de variation */}
      <SectionBackground page="tarifs" section="t-factors" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Ce qui fait varier le tarif</span>
            <h2 className={h2Class}>
              Quatre paramètres,{' '}
              <span className={gradientText}>une transparence totale.</span>
            </h2>
            <p className={sectionSubtitleClass}>
              Le tarif d'une mission de recrutement varie selon ces quatre
              critères. Nous les explicitons toujours dès le premier appel —
              pas de surprise au moment de signer.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {pricingFactors.map((factor, index) => {
              const Icon = factor.icon;
              return (
                <motion.div
                  key={factor.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="rounded-[1.75rem] bg-[#fbfaf8] p-8 ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{factor.title}</h3>
                  <p className={bodyClass}>{factor.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 5 — Pourquoi confidentiel + TalentSafe */}
      <SectionBackground page="tarifs" section="t-why" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className={eyebrowClass}>Pourquoi nos tarifs ne sont pas affichés en façade</span>
              <h2 className={h2Class}>
                On ne vend pas des CV.{' '}
                <span className={gradientText}>On engage notre réputation.</span>
              </h2>
            </div>

            <div className="rounded-[2rem] bg-white p-8 md:p-10 ring-1 ring-neutral-200 shadow-sm space-y-5">
              <p className={bodyClass}>
                recruteurs.io n'est pas un cabinet de recrutement classique.
                Nous engageons notre réputation sur chaque mission. Les tarifs
                varient selon la complexité du poste, votre secteur, et le
                niveau de garantie souhaité — c'est pourquoi nous préférons
                qualifier votre besoin avant d'annoncer un chiffre.
              </p>
              <p className={bodyClass}>
                <strong>Un positionnement exclusif.</strong> Nous combinons
                IA appliquée au sourcing, chasse de tête senior et{' '}
                <a
                  href="/decouvrir"
                  className="font-semibold text-[#d03484] underline-offset-4 hover:underline"
                >
                  Plateforme Parcours Découverte
                </a>{' '}
                propriétaire. Cette combinaison rare se reflète dans la qualité
                des candidats que nous présentons — et dans nos tarifs.
              </p>
              <p className={bodyClass}>
                <strong>Garantie financière incluse.</strong> Grâce à notre
                partenariat avec <strong>TalentSafe</strong>, nos recrutements
                bénéficient d'une garantie financière sur les périodes
                d'essai&nbsp;: en cas d'échec, le placement est soit remboursé,
                soit relancé sans coût supplémentaire. Un engagement fort qui
                protège votre budget et votre planning.
              </p>
              <p className={bodyClass}>
                <strong>Des formules pensées pour chaque entreprise.</strong>{' '}
                Start-up en croissance, PME, ETI ou grand groupe&nbsp;: nos
                offres s'adaptent à vos objectifs. Recrutez mieux, plus vite,
                sans stress — et sans vous ruiner.
              </p>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 6 — CTA final */}
      <SectionBackground page="tarifs" section="t-cta" className="bg-white">
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className={h2Class}>
              Le meilleur moyen de connaître votre tarif ?{' '}
              <span className={gradientText}>Un appel de 15 minutes.</span>
            </h2>
            <p className={`${sectionSubtitleClass} mb-10`}>
              Vous nous décrivez le poste, le contexte, les enjeux. Nous
              chiffrons en transparence — sans engagement de votre côté.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate('/reservation')}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Planifier un appel découverte
              </button>
              <button
                onClick={() => navigate('/decouvrir')}
                className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-6 py-2.5 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
              >
                Voir le Parcours Découverte
              </button>
            </div>
          </div>
        </Section>
      </SectionBackground>

      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </Layout>
  );
}
